import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getAdInsightsV23,
  getAllVaultsForAdmin,
  getMetaAdAccountsV23
} from 'api';
import { Marketplace } from 'helpers/neo4j/neo4jHelper';

import styled, { StyledProps } from 'styled-components';
import NavigationBar from 'components/NavigationBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faSpinner,
  faCoins,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import CustomDatepicker from 'components/CustomDatepicker';
import ChatWidget from 'components/ChatWidget';
import { useAuthState } from 'stores/AuthStore';

const Container = styled.div`
  padding: 20px;
  padding-top: 0;
`;

const MenuButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const Button = styled.button<{ active?: boolean }>`
  padding: 10px 20px;
  background: ${({ active }) => (active ? '#007bff' : '#f0f0f0')};
  color: ${({ active }) => (active ? '#fff' : '#000')};
  border: none;
  border-radius: 5px;
  cursor: pointer;
`;

const SearchInput = styled.input`
  padding: 10px;
  width: 100%;
  max-width: 400px;
  border: 1px solid #ccc;
  border-radius: 5px;
  margin-right: 15px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TR = styled.tr<{ valid?: boolean; leadsFulfilled?: boolean }>`
  background: ${({ valid, leadsFulfilled }) =>
    leadsFulfilled ? '#d4edda' : valid ? '#fff' : '#f8babfff'};
`;

const TH = styled.th<{ sortable?: boolean }>`
  padding: 12px;
  background: #f8f8f8;
  border: 1px solid #ddd;
  cursor: ${({ sortable }) => (sortable ? 'pointer' : 'default')};
`;

const TD = styled.td<StyledProps<{ color?: string }>>`
  padding: 12px;
  border: 1px solid #ddd;
  background: ${({ color }) => color || 'transparent'};
`;

const Extra = styled.div`
  font-size: 0.75rem;
  color: #63b3f5ff;
  font-weight: bold;
  margin-left: 5px;
`;

const HorizontalWrapper = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
`;

const SmallNote = styled.span`
  font-size: 0.9em;
  color: #666;
`;

const Reconciliation = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 20px;
  margin-bottom: 15px;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-left: 4px solid #007bff;
  border-radius: 5px;
  background: #fafafa;
  font-size: 0.9rem;
`;

const ReconciliationValue = styled.span<{ warn?: boolean }>`
  font-weight: bold;
  color: ${({ warn }) => (warn ? '#c0392b' : '#000')};
`;

const StatusWrapper = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  margin-left: auto;
`;

const Status = styled.div`
  margin-left: 10px;
`;

const StatusIndicator = styled(FontAwesomeIcon)<StyledProps<{ done: boolean }>>`
  margin-left: 5px;
  margin-right: 15px;
  color: ${({ done }) => (done ? 'green' : 'grey')};
`;

const PayingIndicator = styled(FontAwesomeIcon)<StyledProps<{ paid: boolean }>>`
  margin-left: 5px;
  margin-right: 15px;
  color: ${({ paid }) => (paid ? 'gold' : 'grey')};
  opacity: ${({ paid }) => (paid ? 1 : 0.2)};
`;

const DatePickerWrapper = styled.div`
  position: absolute;
  top: 60px;
`;

const TimeRangeButtons = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  margin-right: 50px;
  align-items: center;
`;

type Ad = {
  ad_name: string;
  campaign_name: string;
  adset_name: string;
  spend: number;
  impressions: number;
  clicks: number;
  cpc: number;
  ctr: number;
};

type SortConfig = {
  key: keyof Ad;
  direction: 'asc' | 'desc';
};

const MenuOptions = ['Only Meta', 'Only Prompto', 'Prompto x Meta'];

const MetaLeadsOverview = () => {
  const { AuthState } = useAuthState();
  const { sessionToken, user } = AuthState;

  const [activeMenu, setActiveMenu] = useState(MenuOptions[2]);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [isAiChatMode, setIsAiChatMode] = useState(false);

  const [allAds, setAllAds] = useState<any>([]);
  const [allAdsToday, setAllAdsToday] = useState<any>([]);
  const [allAdsFetched, setAllAdsFetched] = useState(false);
  const [failedAdAccounts, setFailedAdAccounts] = useState<any>([]);

  // Bumped by the refresh button to force the fetch effect to run again.
  const [reloadKey, setReloadKey] = useState(0);

  // Every fetch run gets an id. Responses from an older run are dropped instead
  // of being merged into the current data set.
  const runIdRef = useRef(0);

  const [showOnlyInvalidAds, setShowOnlyInvalidAds] = useState(false);

  const [allPromptoLeads, setAllPromptoLeads] = useState<any>([]);
  const [pendingLeads, setPendingLeads] = useState<any>([]);

  const [timeRange, setTimeRange] = useState('this_month');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  const [startDate, setStartDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(null);

  const [allVaults, setAllVaults] = useState<any>(null);

  const handleSort = (key: any) => {
    setSortConfig((prev: any) => {
      if (prev?.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const convertDateToISOString = (date: Date) => {
    const pad = (n: any) => String(n).padStart(2, '0');

    const localDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}`;

    return localDate;
  };

  // Walks every page of one ad account. The returned promise only settles once
  // that account is fully paginated, so the caller can wait for *all* accounts
  // before declaring the Meta data complete.
  const getAdsForAccount = useCallback(
    async (
      runId: number,
      adAccountId: string,
      range: string,
      startDate?: string,
      endDate?: string
    ) => {
      let afterCursor = '';

      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const insightsResponse = await getAdInsightsV23(
            adAccountId,
            afterCursor,
            range,
            startDate,
            endDate
          );

          // A newer fetch started while this page was in flight: drop it,
          // otherwise it gets merged into the new run and doubles the totals.
          if (runId !== runIdRef.current) return;

          const ads = insightsResponse?.data?.data ?? [];

          if (range !== 'today') {
            setAllAds((curr: any) => [...curr, ...ads]);
          } else {
            setAllAdsToday((curr: any) => [...curr, ...ads]);
          }

          const nextCursor = insightsResponse?.data?.paging?.cursors?.after;
          const next = insightsResponse?.data?.paging?.next;

          if (!next || !nextCursor) return;

          afterCursor = nextCursor;
        }
      } catch (error) {
        // Meta throttles heavily when many accounts are queried at once. A
        // dropped page silently under-reports spend, so make it visible rather
        // than letting the totals look complete.
        console.error(
          `Failed to fetch Meta insights for ${adAccountId} (${range})`,
          error
        );
        if (runId === runIdRef.current) {
          setFailedAdAccounts((curr: any) =>
            curr.includes(adAccountId) ? curr : [...curr, adAccountId]
          );
        }
      }
    },
    []
  );

  const getAdSpendForProject = useCallback(
    (project: any) => {
      const adsForProject = project?.projectId
        ? allAds.filter(
            (ad: any) =>
              ad.campaign_name.includes(project.projectId) ||
              ad.adset_name.includes(project.projectId) ||
              ad.ad_name.includes(project.projectId)
          )
        : [];

      const totalSpend = Number(
        adsForProject
          .reduce((acc: number, ad: any) => {
            return acc + (Number(ad.spend) || 0);
          }, 0)
          .toFixed(2)
      );

      return totalSpend ? totalSpend : 0;
    },
    [allAds]
  );

  const getProjectForAd = (ad: any) => {
    const matchingProject = allPromptoLeads.filter(
      (project: any) =>
        ad.campaign_name.includes(project.projectId) ||
        ad.adset_name.includes(project.projectId) ||
        ad.ad_name.includes(project.projectId)
    );

    return matchingProject.length > 0 ? matchingProject[0] : null;
  };

  useEffect(() => {
    if (timeRange === 'custom' && (!startDate || !endDate)) {
      return; // Don't fetch ads if custom date range is selected but no dates are set
    }

    // Claim this run. Anything still in flight from a previous run now has a
    // stale id and will be discarded when it resolves.
    runIdRef.current += 1;
    const runId = runIdRef.current;
    const isCurrentRun = () => runId === runIdRef.current;

    // Reset synchronously, before any request goes out, so a slow response can
    // never land in between the reset and the new data.
    setAllAds([]);
    setAllAdsToday([]);
    setAllAdsFetched(false);
    setFailedAdAccounts([]);
    setAllPromptoLeads([]);

    // Fetch Meta Ads
    getMetaAdAccountsV23()
      .then(async (response) => {
        if (!isCurrentRun()) return;

        const adAccounts = response.data.data; // all accounts

        const accountJobs: Promise<void>[] = [];

        adAccounts.forEach((account: any) => {
          const adAccountId = account.id;

          if (timeRange === 'this_month' || timeRange === 'last_month') {
            accountJobs.push(getAdsForAccount(runId, adAccountId, timeRange));
            accountJobs.push(getAdsForAccount(runId, adAccountId, 'today'));
          }

          if (timeRange === 'custom' && startDate && endDate) {
            accountJobs.push(
              getAdsForAccount(
                runId,
                adAccountId,
                timeRange,
                convertDateToISOString(startDate),
                convertDateToISOString(endDate)
              )
            );
          }
        });

        // Only mark Meta as complete once every account has been paginated to
        // the end -- not when the first (smallest) account happens to finish.
        await Promise.all(accountJobs);

        if (!isCurrentRun()) return;
        setAllAdsFetched(true);
      })
      .catch((error) => {
        console.error('Failed to fetch Meta ad accounts', error);
      });

    // Fetch data from Prompto
    let firstDay, lastDay;
    if (timeRange === 'this_month') {
      const now = new Date();
      firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      lastDay = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );
    }
    if (timeRange === 'last_month') {
      const now = new Date();
      firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      lastDay = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    }
    if (timeRange === 'custom' && startDate && endDate) {
      firstDay = startDate;
      lastDay = endDate;
    }

    Marketplace.getTotalLeadsGeneratedPerProjectThisMonth({
      firstDay,
      lastDay
    }).then((response: any) => {
      if (!isCurrentRun()) return;

      // Handle the response from Prompto
      let formatted: any = [];

      response.forEach((x: any) => {
        const foundProject = formatted.find(
          (y: any) => y.projectId === x.projectId
        );

        if (foundProject) {
          if (x.lead) {
            if (x.lead.rejected) {
              console.log('Rejected lead found:', x.lead);
              foundProject.rejectedLeads += 1;
            } else {
              foundProject.leads.push(x.lead);
              foundProject.leadCount += 1;
            }
          }
        } else {
          const leadToAdd = {
            projectTitle: x.projectTitle,
            projectId: x.projectId,
            vaultName: x.vault.name,
            vaultObjectId: x.vault.objectId,
            targetLeads: x.targetLeads,
            leadCount: 0,
            leads: [] as any[],
            rejectedLeads: 0
          };

          if (x.lead) {
            if (x.lead.rejected) {
              leadToAdd.rejectedLeads = 1;
            } else {
              leadToAdd.leads.push(x.lead);
              leadToAdd.leadCount = 1;
            }
          }

          formatted.push(leadToAdd);
        }
      });

      formatted = formatted.map((project: any) => ({
        ...project,
        percentage:
          project.targetLeads > 0
            ? ((project.leadCount / project.targetLeads) * 100).toFixed(0)
            : 0
      }));

      setAllPromptoLeads(formatted);
    });

    Marketplace.getPendingLeads().then((response: any) => {
      if (!isCurrentRun()) return;
      setPendingLeads(response);
    });

    if (user?.vaultList?.[0]) {
      getAllVaultsForAdmin(user.vaultList[0].objectId, sessionToken).then(
        (result) => {
          if (!isCurrentRun()) return;
          setAllVaults(result.data.vaultList);
        }
      );
    }
  }, [
    reloadKey,
    timeRange,
    startDate,
    endDate,
    user,
    sessionToken,
    getAdsForAccount
  ]);

  const filteredAds = useMemo(() => {
    return allAds
      .filter((ad: any) =>
        [ad.ad_name, ad.campaign_name, ad.adset_name]
          .join(' ')
          .toLowerCase()
          .includes(search.toLowerCase())
      )
      .sort((a: any, b: any) => {
        if (!sortConfig) return 0;

        const key = sortConfig.key;
        const isNumericKey = [
          'spend',
          'impressions',
          'clicks',
          'cpc',
          'ctr'
        ].includes(key);
        const aVal = isNumericKey
          ? Number(a[key])
          : String(a[key]).toLowerCase();
        const bVal = isNumericKey
          ? Number(b[key])
          : String(b[key]).toLowerCase();

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
  }, [search, sortConfig, allAds]);

  const isFullyLoaded = allAdsFetched && allPromptoLeads.length > 0;

  // Derived, not stored in state: recomputes whenever ads / vaults / leads
  // change, so a partially loaded data set self-corrects once the rest lands
  // instead of being latched in permanently.
  const enhancedProjects = useMemo(() => {
    if (
      !allAdsFetched ||
      !(allVaults?.length > 0) ||
      allAds.length === 0 ||
      allPromptoLeads.length === 0
    ) {
      return allPromptoLeads;
    }

    return allPromptoLeads.map((project: any) => {
      const vaultForProject = allVaults.find(
        (vault: any) => vault.objectId === project.vaultObjectId
      );

      const isAlternativePricing =
        vaultForProject?.settings.leadInvoiceSettings?.useAlternativePricing;

      const isPayingForLeads =
        vaultForProject?.settings.leadInvoiceSettings
          ?.isPayingForLeadGeneration ?? true;

      const investPrice = isPayingForLeads
        ? isAlternativePricing
          ? 75
          : 70
        : 0;
      const personalPrice = isPayingForLeads
        ? isAlternativePricing
          ? 65
          : 50
        : 0;

      let oldInvest = 0;
      let oldPersonal = 0;

      const totalLeadRevenue = project.leads.reduce(
        (acc: number, lead: any) => {
          // Hardcoded timestamp, we updated prices in feb 2026
          if (lead.createdAt < 1769900399000) {
            if (lead.isForInvestment) {
              oldInvest += 1;
            } else {
              oldPersonal += 1;
            }
            return acc + (lead.isForInvestment ? 65 : 45);
          }

          return acc + (lead.isForInvestment ? investPrice : personalPrice);
        },
        0
      );

      const investLeads = project.leads.filter((x: any) => x.isForInvestment);
      const isPersonal = project.leads.filter(
        (x: any) => x.isForPersonalUse && !x.isForInvestment
      );

      let calculatedString = `(${investLeads?.length - oldInvest} * €${investPrice}) + (${isPersonal.length - oldPersonal} * €${personalPrice})`;
      if (oldInvest > 0 || oldPersonal > 0) {
        calculatedString += ` + (${oldInvest} * €65) + (${oldPersonal} * €45)`;
      }

      const adSpendForProject = getAdSpendForProject(project);

      return {
        ...project,
        calculatedString,
        vault: vaultForProject,
        revenue: totalLeadRevenue,
        adSpend: adSpendForProject,
        profit: Number((totalLeadRevenue - adSpendForProject).toFixed(2))
      };
    });
  }, [
    allAdsFetched,
    allVaults,
    allAds,
    allPromptoLeads,
    getAdSpendForProject
  ]);

  const filterProjects = useMemo(() => {
    return enhancedProjects
      .filter((project: any) => {
        return (
          (project.adSpend === 0 &&
            project.leads.length === 0 &&
            project.targetLeads === 0) === false
        );
      })
      .filter((project: any) =>
        [project.projectTitle, project.vaultName]
          .join(' ')
          .toLowerCase()
          .includes(search.toLowerCase())
      )
      .sort((a: any, b: any) => {
        if (!sortConfig) return 0;
        const key = sortConfig.key;

        const isNumericKey = [
          'leadCount',
          'targetLeads',
          'percentage',
          'adSpend',
          'revenue',
          'profit'
        ].includes(key);

        const aVal = isNumericKey
          ? Number(a[key])
          : String(a[key]).toLowerCase();
        const bVal = isNumericKey
          ? Number(b[key])
          : String(b[key]).toLowerCase();

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
  }, [enhancedProjects, search, sortConfig]);

  // Everything Meta billed us for, across every ad account and page.
  const totalMetaSpend = useMemo(
    () =>
      Number(
        allAds
          .reduce((acc: number, ad: any) => acc + (Number(ad.spend) || 0), 0)
          .toFixed(2)
      ),
    [allAds]
  );

  // Only the spend we could tie back to a project, i.e. ads whose
  // campaign/adset/ad name contains a projectId that has leads this period.
  // Deliberately computed off enhancedProjects, not filterProjects, so the
  // search box doesn't distort the reconciliation.
  const attributedSpend = useMemo(
    () =>
      Number(
        enhancedProjects
          .reduce(
            (acc: number, project: any) => acc + (Number(project.adSpend) || 0),
            0
          )
          .toFixed(2)
      ),
    [enhancedProjects]
  );

  const unattributedSpend = Number(
    (totalMetaSpend - attributedSpend).toFixed(2)
  );

  const checkHasAdValidNaming = (ad: any) => {
    const regex = /^([^_]+)_.*_([^_]+)$/;
    const matchCampaignName = ad.campaign_name.match(regex);

    if (matchCampaignName) {
      const firstId = matchCampaignName[1];
      const secondId = matchCampaignName[2];

      if (firstId && secondId) {
        return true;
      }
    }

    const matchAdSetName = ad.adset_name.match(regex);
    if (matchAdSetName) {
      const firstId = matchAdSetName[1];
      const secondId = matchAdSetName[2];

      if (firstId && secondId) {
        return true;
      }
    }

    const matchAdName = ad.ad_name.match(regex);
    if (matchAdName) {
      const firstId = matchAdName[1];
      const secondId = matchAdName[2];

      if (firstId && secondId) {
        return true;
      }
    }

    return false;
  };

  const getFadedColor = (percent: number) => {
    // Clamp percent to [0, 100]
    percent = Math.max(0, Math.min(100, percent));

    // Convert percent to a 0–1 range
    const ratio = percent / 100;

    // Red decreases from 180 to 80
    const red = Math.round(180 - 100 * ratio);

    // Green increases from 80 to 180
    const green = Math.round(80 + 100 * ratio);

    // Keep blue low for a muted tone
    const blue = 80;

    return `rgba(${red}, ${green}, ${blue}, 0.75)`;
  };

  if (isAiChatMode) {
    return (
      <Container>
        <NavigationBar />
        <ChatWidget onClose={() => setIsAiChatMode(false)} />
      </Container>
    );
  }

  return (
    <Container>
      <NavigationBar />
      <HorizontalWrapper>
        <MenuButtons>
          {MenuOptions.map((menu) => (
            <Button
              key={menu}
              active={activeMenu === menu}
              onClick={() => setActiveMenu(menu)}
            >
              {menu}
            </Button>
          ))}
          <Button onClick={() => setIsAiChatMode(true)}>AI Assistant</Button>
        </MenuButtons>
        <StatusWrapper>
          <TimeRangeButtons>
            <Button
              active={timeRange === 'this_month'}
              onClick={() => setTimeRange('this_month')}
            >
              This month
            </Button>
            <Button
              active={timeRange === 'last_month'}
              onClick={() => setTimeRange('last_month')}
            >
              Last month
            </Button>
            <Button
              active={timeRange === 'custom'}
              onClick={() => {
                setTimeRange('custom');
                setStartDate(null);
                setEndDate(null);
                setShowCustomDatePicker((prev) => !prev);
              }}
            >
              Custom range
            </Button>
            <div>
              {timeRange === 'this_month' && (
                <span>
                  {new Date().toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              )}
              {timeRange === 'last_month' && (
                <span>
                  {new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1
                  ).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              )}
              {timeRange === 'custom' && (
                <span>
                  {startDate
                    ? `${startDate.toLocaleDateString('nl-BE')} - ${
                        endDate
                          ? endDate.toLocaleDateString('nl-BE')
                          : 'Select end date'
                      }`
                    : 'Select a date range'}
                </span>
              )}
            </div>
          </TimeRangeButtons>

          {showCustomDatePicker && (
            <DatePickerWrapper>
              <CustomDatepicker
                onChange={(dates: any) => {
                  const [start, end] = dates;
                  setStartDate(start);
                  setEndDate(null);
                  if (end) {
                    setEndDate(end);
                    setShowCustomDatePicker(false);
                  }
                }}
                value={startDate}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                enableMaxDate={true}
                inline
              />
            </DatePickerWrapper>
          )}

          <Status>Data loaded:</Status>
          <Status>Prompto</Status>
          <StatusIndicator
            data-testid="sortByIcon"
            // @ts-ignore
            icon={allPromptoLeads?.length > 0 ? faCheck : faSpinner}
            size="1x"
            done={allPromptoLeads?.length > 0}
            pulse={!(allPromptoLeads?.length > 0)}
          />
          <Status>Meta</Status>
          <StatusIndicator
            data-testid="sortByIcon"
            // @ts-ignore
            icon={
              allAdsFetched && failedAdAccounts.length > 0
                ? faTriangleExclamation
                : allAdsFetched
                  ? faCheck
                  : faSpinner
            }
            size="1x"
            done={allAdsFetched && failedAdAccounts.length === 0}
            pulse={!allAdsFetched}
            title={
              failedAdAccounts.length > 0
                ? `Incomplete: ${failedAdAccounts.length} ad account(s) failed to load (${failedAdAccounts.join(
                    ', '
                  )}). Spend is under-reported -- refresh to retry.`
                : undefined
            }
          />
          {/* One trigger: the fetch effect owns resetting every piece of state,
              so refresh can never leave a half-cleared data set behind. Stays
              clickable while loading -- a new run invalidates the old one. */}
          <Button onClick={() => setReloadKey((prev) => prev + 1)}>
            {isFullyLoaded ? 'Refresh data' : 'Loading…'}
          </Button>
        </StatusWrapper>
      </HorizontalWrapper>

      {/* Reconciles what Meta billed against what we could tie to a project.
          Any gap is spend on ads whose name carries no known projectId, so it
          never lands in the Marge/profit columns. */}
      {allAdsFetched && (
        <Reconciliation>
          <span>
            Meta spend fetched:{' '}
            <ReconciliationValue>
              € {totalMetaSpend.toFixed(2)}
            </ReconciliationValue>{' '}
            <SmallNote>({allAds.length} ads)</SmallNote>
          </span>
          <span>
            Attributed to projects:{' '}
            <ReconciliationValue>
              € {attributedSpend.toFixed(2)}
            </ReconciliationValue>
          </span>
          <span
            title={
              'Spend on ads whose campaign/adset/ad name contains no projectId that generated leads in this period. ' +
              'Check "Only Meta" > "Show Only Invalid Ads" to find them.'
            }
          >
            Unattributed:{' '}
            <ReconciliationValue warn={Math.abs(unattributedSpend) > 0.01}>
              € {unattributedSpend.toFixed(2)}
            </ReconciliationValue>
          </span>
          {failedAdAccounts.length > 0 && (
            <ReconciliationValue warn>
              {failedAdAccounts.length} ad account(s) failed to load — totals are
              incomplete
            </ReconciliationValue>
          )}
        </Reconciliation>
      )}

      <HorizontalWrapper>
        <SearchInput
          type="text"
          placeholder="Search by ad/campaign/set name..."
          value={search}
          onChange={(e: any) => setSearch(e.target.value)}
        />
        {activeMenu === 'Only Meta' && (
          <Button onClick={() => setShowOnlyInvalidAds((prev) => !prev)}>
            {showOnlyInvalidAds ? 'Show All Ads' : 'Show Only Invalid Ads'}
          </Button>
        )}
      </HorizontalWrapper>

      {activeMenu === 'Only Meta' && (
        <Table>
          <thead>
            <tr>
              <th></th>
              <th></th>
              <th></th>
              <th>
                {filteredAds
                  .reduce(
                    (acc: number, ad: any) => acc + (Number(ad.spend) || 0),
                    0
                  )
                  .toFixed(2)}
              </th>
              <th>
                {filteredAds
                  .reduce(
                    (acc: number, ad: any) =>
                      acc + (Number(ad.impressions) || 0),
                    0
                  )
                  .toFixed(0)}
              </th>
              <th>
                {filteredAds
                  .reduce(
                    (acc: number, ad: any) => acc + (Number(ad.clicks) || 0),
                    0
                  )
                  .toFixed(0)}
              </th>
            </tr>
            <tr>
              <TH sortable onClick={() => handleSort('campaign_name')}>
                Campaign
              </TH>
              <TH sortable onClick={() => handleSort('adset_name')}>
                Ad Set
              </TH>
              <TH sortable onClick={() => handleSort('ad_name')}>
                Ad Name
              </TH>
              <TH sortable onClick={() => handleSort('spend')}>
                Spend
              </TH>
              <TH sortable onClick={() => handleSort('impressions')}>
                Impressions
              </TH>
              <TH sortable onClick={() => handleSort('clicks')}>
                Clicks
              </TH>
              <TH sortable onClick={() => handleSort('cpc')}>
                CPC
              </TH>
              <TH sortable onClick={() => handleSort('ctr')}>
                CTR
              </TH>
            </tr>
          </thead>
          <tbody>
            {filteredAds.map((ad: any, index: any) => {
              const isAdValid = checkHasAdValidNaming(ad);

              if (showOnlyInvalidAds && isAdValid) {
                return null; // Skip rendering this ad if it doesn't have valid naming}
              }

              const project = getProjectForAd(ad);

              const matchingAdForToday = allAdsToday.find(
                (adx: any) => adx.ad_name === ad.ad_name
              );
              const activeAd = Number(matchingAdForToday?.spend) > 0;

              const leadsFulfilled =
                (project?.targetLeads === 0 ||
                  project?.leads.length >= project?.targetLeads) &&
                activeAd;

              return (
                <TR
                  key={index}
                  valid={isAdValid && project}
                  leadsFulfilled={leadsFulfilled}
                >
                  <TD>{ad.campaign_name}</TD>
                  <TD>{ad.adset_name}</TD>
                  <TD>{ad.ad_name}</TD>
                  <TD>€{ad.spend}</TD>
                  <TD>{ad.impressions}</TD>
                  <TD>{ad.clicks}</TD>
                  <TD>€{ad.cpc}</TD>
                  <TD>{ad.ctr}%</TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      )}

      {activeMenu === 'Only Prompto' && (
        <Table>
          <thead>
            <tr>
              <TH sortable onClick={() => handleSort('vaultName')}>
                Client
              </TH>
              <TH sortable onClick={() => handleSort('title')}>
                Project Title
              </TH>
              <TH sortable onClick={() => handleSort('leadCount')}>
                Leads Generated
              </TH>
              <TH sortable onClick={() => handleSort('targetLeads')}>
                Target Leads
              </TH>
              <TH sortable onClick={() => handleSort('percentage')}>
                % of Target
              </TH>
            </tr>
          </thead>
          <tbody>
            {filterProjects.map((project: any, index: number) => {
              return (
                <TR key={index} valid={true}>
                  <TD>
                    {project.vaultName}{' '}
                    <SmallNote>({project.vaultObjectId})</SmallNote>
                  </TD>
                  <TD>
                    {project.projectTitle}{' '}
                    <SmallNote>({project.projectId})</SmallNote>
                  </TD>
                  <TD>{project.leads.length}</TD>
                  <TD>{project.targetLeads}</TD>
                  <TD>{project.percentage}%</TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      )}

      {activeMenu === 'Prompto x Meta' && (
        <Table>
          <thead>
            <tr>
              <th></th>
              <th></th>
              <TH>
                {filterProjects
                  .reduce(
                    (acc: number, project: any) =>
                      acc + (Number(project.leads.length) || 0),
                    0
                  )
                  .toFixed(0)}
              </TH>
              <TH>
                {filterProjects
                  .reduce(
                    (acc: number, project: any) =>
                      acc + (Number(project.rejectedLeads) || 0),
                    0
                  )
                  .toFixed(0)}
              </TH>
              <TH>
                {filterProjects
                  .reduce(
                    (acc: number, project: any) =>
                      acc + (Number(project.targetLeads) || 0),
                    0
                  )
                  .toFixed(0)}
              </TH>
              <TH>
                {(
                  filterProjects.reduce(
                    (acc: number, project: any) =>
                      acc + (Number(project.percentage) || 0),
                    0
                  ) / filterProjects.length
                ).toFixed(0)}
                %
              </TH>
              <TH>
                €{' '}
                {filterProjects
                  .reduce(
                    (acc: number, project: any) => acc + (Number(project.adSpend) || 0),
                    0
                  )
                  .toFixed(2)}
              </TH>
              <TH>
                €{' '}
                {filterProjects
                  .reduce(
                    (acc: number, project: any) => acc + (Number(project.revenue) || 0),
                    0
                  )
                  .toFixed(2)}
              </TH>
              <TH>
                €{' '}
                {filterProjects
                  .reduce(
                    (acc: number, project: any) => acc + (Number(project.profit) || 0),
                    0
                  )
                  .toFixed(2)}
              </TH>
              <TH>
                %{' '}
                {(
                  (Number(
                    filterProjects.reduce(
                      (acc: number, project: any) => acc + (Number(project.profit) || 0),
                      0
                    )
                  ) /
                    Number(
                      filterProjects.reduce(
                        (acc: number, project: any) =>
                          acc + (Number(project.revenue) || 0),
                        0
                      )
                    )) *
                  100
                ).toFixed(2) || 0}
              </TH>
            </tr>
            <tr>
              <TH sortable onClick={() => handleSort('vaultName')}>
                Client
              </TH>
              <TH sortable onClick={() => handleSort('title')}>
                Project Title
              </TH>
              <TH sortable onClick={() => handleSort('leadCount')}>
                Leads Generated
              </TH>
              <TH sortable onClick={() => handleSort('rejectedLeads')}>
                Leads Disputed
              </TH>
              <TH sortable onClick={() => handleSort('targetLeads')}>
                Target Leads
              </TH>
              <TH sortable onClick={() => handleSort('percentage')}>
                % of Target
              </TH>
              <TH sortable onClick={() => handleSort('adSpend')}>
                Ad Spend
              </TH>
              <TH sortable onClick={() => handleSort('revenue')}>
                Revenue
              </TH>
              <TH sortable onClick={() => handleSort('profit')}>
                Marge
              </TH>
              <TH sortable onClick={() => handleSort('profitMargin')}>
                Profit margin
              </TH>
            </tr>
          </thead>
          <tbody>
            {filterProjects.map((project: any, index: number) => {
              let colorForPercentage = getFadedColor(project.percentage);
              if (project.targetLeads === 0) {
                colorForPercentage = '#28b9e6ff'; // Grey out if no target
              }

              let colorForProfit =
                project.profit < 0 ? getFadedColor(0) : getFadedColor(100);

              const pendingLeadsForProject = pendingLeads.filter((lead: any) =>
                lead.additionalInfo.projectId.includes(project.projectId)
              );

              return (
                <TR key={index} valid={true}>
                  {/* <TD>{index}</TD> */}
                  <TD>
                    <PayingIndicator
                      data-testid="sortByIcon"
                      // @ts-ignore
                      icon={faCoins}
                      paid={
                        project.vault?.settings.leadInvoiceSettings
                          ?.isPayingForLeadGeneration ?? true
                      }
                      size="1x"
                    />
                    {project.vaultName}{' '}
                    <SmallNote>({project.vaultObjectId})</SmallNote>
                  </TD>
                  <TD>
                    <a
                      href={`https://prompto.com/vault/${project.vaultObjectId}/project/${project.projectId}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {project.projectTitle}{' '}
                    </a>
                    <SmallNote>({project.projectId})</SmallNote>
                  </TD>
                  <TD>
                    <HorizontalWrapper>
                      <div>{project.leads.length}</div>
                      {pendingLeadsForProject.length > 0 && (
                        <Extra>{` ( +${pendingLeadsForProject.length} )`}</Extra>
                      )}
                    </HorizontalWrapper>
                  </TD>
                  <TD>{project.rejectedLeads}</TD>
                  <TD>{project.targetLeads}</TD>
                  <TD color={colorForPercentage}>{project.percentage}%</TD>
                  <TD>€ {project.adSpend}</TD>
                  <TD title={project.calculatedString}>€ {project.revenue}</TD>
                  <TD color={colorForProfit}>€ {project.profit}</TD>
                  <TD>
                    %{' '}
                    {((project.profit / project.revenue) * 100).toFixed(2) || 0}
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default MetaLeadsOverview;
